interface OffspringCheckParams {
    ia: any[];
    parent: string;
    key?: string;
    id: string;
}

interface IdOffsetParams {
    ia: any[];
    id: string;
    io: any;
    offset?: number;
}

interface KidInfo {
    generation: number;
    child: number;
    kids: string[];
    chrono: number;
}

interface FamilyTreeNode {
    id: string;
    name?: string;
    parent?: string;
    siblings: number;
    generation: number;
    child: number;
    chronological: number;
    offsprings: string[];
    older_siblings_offsprings: string[];
}

interface KeyMinSecondaryResult {
    i: number;
    k1: number;
    k2: number;
}

interface FamilyOrderParams {
    node: any;
    parent?: string;
    generation: number;
    child: number;
    siblings?: number;
    offsprings: string[];
    older_siblings_offsprings: string[];
}

export const Arry = {
    validate: function (ia: any): ia is any[] {
        return Array.isArray(ia);
    },

    clone: function <T>(ia: T): T {
        return JSON.parse(JSON.stringify(ia));
    },

    shuffle: function <T>(arr: T[]): T[] {
        const a = arr.slice();
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
    },

get: {
    index: function (db: any[], id: string): number {
        return db.findIndex(function (el) {
            if (el.id) return el.id === id;
            return false;
        });
    },
    string_index: function (ia: string[], id: string): number {
        return ia.findIndex(function (el) { return el == id; });
    },
    id: function (db: any[], id: string): any | undefined {
        return db.find(element => element.id === id) || undefined;
    },
    ids: function (db: any[], ids: string[]): any[] {
        return db.filter(item => ids.includes(item.id!));
    },
    uuid: function (db: any[], uuid: string): any | undefined {
        return db.find(element => element.uuid === uuid);
    },
    key: function (db: any[], key: string, match: any): any | undefined {
        return db.find(element => element[key] === match);
    },
    keys_match: function (db: any[], search_keys: any = { id: "fart", branch: "trousers" }): any | undefined {
        return db.find(element => {
            return Object.keys(search_keys).every(key =>
                element[key] === search_keys[key]
            );
        });
    },
    ids_as_string: function (db: any[]): string[] {
        return db.map(item => item.id!.toString());
    },
    strings_from: function (db: string[], id: string): string[] {
        const index = Arry.get.string_index(db, id);
        if (index === -1) return [];
        return db.slice(index);
    },
    strings_after: function (db: string[], id: string): string[] {
        const index = Arry.get.string_index(db, id);
        return db.slice(index + 1);
    },
    type: function (db: any[], type: string): any | undefined {
        return db.find(element => element.type === type);
    },
    random: function (db: any[]): any {
        return db[Math.floor(Math.random() * db.length)];
    },
    shift: function (arr: any[], n: number = 1): any[] {
        const shift = n % arr.length;
        if (shift === 0) return [...arr];
        return [...arr.slice(shift), ...arr.slice(0, shift)];
    },
    randomWithKey: function (arr: any[], key: string, value: any): any | null {
        const filtered = arr.filter(obj => obj.hasOwnProperty(key) && obj[key] === value);
        if (filtered.length === 0) return null;
        const randomIndex = Math.floor(Math.random() * filtered.length);
        return filtered[randomIndex];
    },
    children: function (array: any[], parent_id: string): string[] {
        const children: string[] = [];
        const queue: string[] = [parent_id];
        const seen = new Set<string>();

        while (queue.length > 0) {
            const currentId = queue.shift()!;
            const directChildren = array.filter(obj =>
                obj.parent && obj.parent.id === currentId
            );

            for (const child of directChildren) {
                if (!seen.has(child.id!)) {
                    children.push(child.id!);
                    seen.add(child.id!);
                    queue.push(child.id!);
                }
            }
        }
        return children;
    },
    parent: function (db: any[], child_id: string): any | undefined {
        for (const n of db) {
            if (n.kids && n.kids.includes(child_id)) return n;
        }
        return undefined;
    },
    parent_with_key: function (db: any[], child_id: string, key: string, key_value: any): any | undefined {
        for (const n of db) {
            if (n.kids && n.kids.includes(child_id) && n[key] !== undefined && n[key] === key_value) return n;
        }
        return undefined;
    },
    offspring: function (db: any[], start_id: string = "root"): string[] {
        const collect: string[] = [];
        function searching(io: any): void {
            if (io.id !== start_id) collect.push(io.id!);
            if (io.kids) {
                for (let i = 0; i < io.kids.length; i++) {
                    const kid = Arry.get.id(db, io.kids[i]);
                    if (kid) searching(kid as any);
                }
            }
        }
        const root = Arry.get.id(db, start_id);
        if (root) searching(root as any);
        return collect;
    },
    kid_info: function (db: any[], kid_id: string = "color", start_id: string = "root"): KidInfo | undefined {
        let chrono = 0;
        function order(io: any, generation: number, child: number): KidInfo | undefined {
            chrono++;
            if (io.id == kid_id) return { generation, child, kids: Arry.get.offspring(db, io.id!), chrono };
            if (io.kids) {
                for (let i = 0; i < io.kids.length; i++) {
                    const kid = Arry.get.id(db, io.kids[i]);
                    if (kid) {
                        const result = order(kid as any, generation + 1, i);
                        if (result) return result;
                    }
                }
            }
            return undefined;
        }
        const root = Arry.get.id(db, start_id);
        if (root) return order(root as any, 0, 0);
        return undefined;
    },
    family_info: function (db: any[], start_id: string = "root"): FamilyTreeNode[] {
        const tree: FamilyTreeNode[] = [];
        function order(params: FamilyOrderParams): void {
            const { node, parent, generation, child, siblings, offsprings, older_siblings_offsprings } = params;
            tree.push({
                id: node.id!,
                name: node.name,
                parent,
                siblings: siblings || 0,
                generation,
                child,
                chronological: tree.length,
                offsprings,
                older_siblings_offsprings
            });
            if (node.kids) {
                const siblings_offsprings: string[] = [];
                for (let i = 0; i < node.kids.length; i++) {
                    const kid = Arry.get.id(db, node.kids[i]);
                    if (kid) {
                        const node_offspring = Arry.get.offspring(db, kid.id!);
                        order({
                            node: kid as any,
                            parent: node.id,
                            generation: generation + 1,
                            child: i,
                            siblings: node.kids.length,
                            offsprings: node_offspring,
                            older_siblings_offsprings: Arry.clone(siblings_offsprings)
                        });
                        siblings_offsprings.push(...node_offspring);
                    }
                }
            }
        }
        const root = Arry.get.id(db, start_id);
        if (root) order({ node: root as any, generation: 0, child: 0, offsprings: Arry.get.offspring(db, root.id!), older_siblings_offsprings: [] });
        return tree;
    },
    min: function (db: number[]): number {
        return Math.min(...db);
    },
    max: function (db: number[]): number {
        return Math.max(...db);
    },
    keyMin: function (db: any[], key: string): any {
        let min = db[0][key];
        for (let i = 1; i < db.length; i++) {
            if (db[i][key] < min) min = db[i][key];
        }
        return min;
    },
    keyMinIndex: function (db: any[], key: string): number {
        let min = db[0][key];
        let index = 0;
        for (let i = 1; i < db.length; i++) {
            if (db[i][key] < min) {
                min = db[i][key];
                index = i;
            }
        }
        return index;
    },
    keyMinSecondaryIndex: function (db: any[], key1: string, key2: string): KeyMinSecondaryResult {
        let collect: KeyMinSecondaryResult[] = [];
        const min1 = Arry.get.keyMin(db, key1);
        for (let i = 0; i < db.length; i++) {
            if (db[i][key1] == min1) collect.push({ i: i, k1: min1, k2: db[i][key2] });
        }
        Arry.sort.key(collect as any, "k2");
        return collect[0];
    },
    keyMinNode: function (db: any[], key: string): any {
        let min = db[0][key];
        let index = 0;
        for (let i = 1; i < db.length; i++) {
            if (db[i][key] < min) {
                min = db[i][key];
                index = i;
            }
        }
        return db[index];
    },
    keyMaxNode: function (db: any[], key: string): any {
        let max = db[0][key];
        let index = 0;
        for (let i = 1; i < db.length; i++) {
            if (db[i][key] > max) {
                max = db[i][key];
                index = i;
            }
        }
        return db[index];
    },
    keyKeyMin: function (db: any[], key1: string, key2: string): any {
        let min = db[0][key1][key2];
        for (let i = 1; i < db.length; i++) {
            if (db[i][key1][key2] < min) min = db[i][key1][key2];
        }
        return min;
    },
    keyMax: function (db: any[], key: string): any {
        let max = db[0][key];
        for (let i = 1; i < db.length; i++) {
            if (db[i][key] > max) max = db[i][key];
        }
        return max;
    },
    keyKeyMax: function (db: any[], key1: string, key2: string): any {
        let max = db[0][key1][key2];
        for (let i = 1; i < db.length; i++) {
            if (db[i][key1][key2] > max) max = db[i][key1][key2];
        }
        return max;
    },
    uniqueValues: function <T>(db: T[]): T[] {
        return [...new Set(db)];
    }
},

remove: {
    id: function (db: any[], id: string): any | undefined {
        const index = Arry.get.index(db, id);
        if (index !== -1) {
            const removedItem = db[index];
            db.splice(index, 1);
            return removedItem;
        }
        return undefined;
    },
    ids: function (db: any[], id_string: string[]): any[] {
        for (const id of id_string) {
            this.id(db, id);
        }
        return db;
    },
    others: function (db: any[], allowed_ids: string[]): any[] {
        for (let i = db.length - 1; i >= 0; i--) {
            if (!allowed_ids.includes(db[i].id!)) db.splice(i, 1);
        }
        return db;
    },
    id_with_key: function (db: any[], id: string, key: string = "color", key_value: any = "red"): any | undefined {
        const index = Arry.get.index(db, id);
        if (index !== -1) {
            if (db[index][key] === key_value) {
                const removedItem = db[index];
                db.splice(index, 1);
                return removedItem;
            }
        }
        return undefined;
    },
    key_includes: function (db: any[], key: string = "color", value: any = "red"): any[] {
        for (const e of db) {
            if (e[key] && e[key].includes(value)) this.string(e[key], value);
        }
        return db;
    },
    orphans: function (db: any[], root_id: string = "root"): void {
        const all_kids_ids = Arry.get.children(db, root_id);
        Arry.remove.others(db, all_kids_ids);
    },
    random: function (db: any[]): any {
        const index = Math.floor(Math.random() * db.length);
        return db.splice(index, 1)[0];
    },
    index: function (db: any[], index: number): any {
        return db.splice(index, 1)[0];
    },
    string: function (db: string[], is: string): string[] {
        const index = db.indexOf(is);
        if (index !== -1) db.splice(index, 1);
        return db;
    }
},

move: {
    string: function (db: string[], id: string, direction: string = "left"): string[] {
        const index = db.indexOf(id);
        if (index === -1) return db.slice();
        const newArr = db.slice();
        if (direction == "left") {
            if (index === 0) return db.slice();
            [newArr[index - 1], newArr[index]] = [newArr[index], newArr[index - 1]];
        } else {
            if (index === newArr.length - 1) return newArr;
            [newArr[index], newArr[index + 1]] = [newArr[index + 1], newArr[index]];
        }
        return newArr;
    },
    index: function (db: any[], from: number, to: number): any[] {
        if (from === to) return db;

        const normalizedFrom = from < 0 ? Math.max(db.length + from, 0) : Math.min(from, db.length - 1);
        const normalizedTo = to < 0 ? Math.max(db.length + to, 0) : Math.min(to, db.length - 1);

        const moving_element = db.splice(normalizedFrom, 1)[0];
        db.splice(normalizedTo, 0, moving_element);
        return db;
    },
    id: function (db: any[], id: string, index: number): any[] {
        const now = Arry.get.index(db, id);
        const moving_element = db.splice(now, 1)[0];
        db.splice(index, 0, moving_element);
        return db;
    },
    switch_ids: function (db: any[], id1: string, id2: string): any[] {
        const index1 = db.findIndex(obj => obj.id === id1);
        const index2 = db.findIndex(obj => obj.id === id2);
        if (index1 === -1 || index2 === -1) return db;
        [db[index1], db[index2]] = [db[index2], db[index1]];
        return db;
    },
    after_id: function (db: any[], moving_id: string, reference_id: string): any[] {
        const refIndex = Arry.get.index(db, reference_id);
        const movingIndex = Arry.get.index(db, moving_id);
        if (refIndex > -1 && movingIndex > -1) {
            const [movedItem] = db.splice(movingIndex, 1);
            const newIndex = movingIndex < refIndex ? refIndex : refIndex + 1;
            db.splice(newIndex, 0, movedItem);
        }
        return db;
    },
    before_id: function (db: any[], moving_id: string, reference_id: string): any[] {
        const refIndex = Arry.get.index(db, reference_id);
        const movingIndex = Arry.get.index(db, moving_id);
        if (refIndex > -1 && movingIndex > -1) {
            const [movedItem] = db.splice(movingIndex, 1);
            const newIndex = movingIndex < refIndex ? refIndex - 1 : refIndex;
            db.splice(newIndex, 0, movedItem);
        }
        return db;
    },
    up: function (db: any[], id: string): any[] {
        const index = Arry.get.index(db, id);
        if (index > -1 && index < db.length - 1) {
            Arry.move.index(db, index, index + 1);
        }
        return db;
    },
    down: function (db: any[], id: string): any[] {
        const index = Arry.get.index(db, id);
        if (index > 0 && index < db.length) {
            Arry.move.index(db, index, index - 1);
        }
        return db;
    }
},

filter: {
    id: function (db: any[], id: string): any[] {
        return db.filter(e => e.id !== id);
    },
    ids: function (db: any[], ids: string[]): any[] {
        return db.filter(e => !ids.includes(e.id!));
    },
    type: function (db: any[], type: string): any[] {
        return db.filter(element => element.type === type);
    },
    string: function (db: string[], id: string): string[] {
        return db.filter(item => item !== id);
    },
    ignore: function <T extends object>(db: T, keys: string[] = ["gl", "fart"]): T {
        function digger(io: any): any {
            if (!io || typeof io !== 'object') return io;
            if (Array.isArray(io)) return io.map(entry => digger(entry)).filter(entry => entry !== undefined);
            const result: any = {};
            for (const id in io) {
                if (!keys.includes(id)) {
                    const value = digger(io[id]);
                    if (value !== undefined) {
                        result[id] = value;
                    }
                }
            }
            return result;
        }
        return digger(db);
    },
    ignore_key: function (db: any[], key: string = "export_ignore"): any[] {
        function filterObject(obj: any, ignoreList: string[] = []): any {
            if (!obj || typeof obj !== 'object') return obj;
            const currentIgnore = [...ignoreList, ...(obj.export_ignore || [])];
            const result: any = {};
            let hasChanges = false;

            for (const k in obj) {
                if (k === key) continue;
                if (currentIgnore.includes(k)) {
                    hasChanges = true;
                    continue;
                }
                const value = obj[k];
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    const filteredValue = filterObject(value, currentIgnore);
                    if (filteredValue !== value) hasChanges = true;
                    result[k] = filteredValue;
                } else {
                    result[k] = value;
                }
            }
            return hasChanges ? result : obj;
        }
        return db.map(item => filterObject(item));
    },
    key: function (db: any[], key: string): any[] {
        return db.filter(function (el) {
            return el[key] !== undefined;
        });
    },
    keys: function (db: any[], keys: string[]): Partial<any>[] {
        return db.map(item =>
            keys.reduce((obj, key) => {
                if (key in item) obj[key] = item[key];
                return obj;
            }, {} as Partial<any>)
        );
    },
    ids_with_key: function (db: any[], ids: string[], key: string, key_value: any): any[] {
        return db.filter(item => ids.includes(item.id!) && item[key] !== undefined && item[key] === key_value);
    },
    keyIs: function (db: any[], key: string, reference: any): any[] {
        return db.filter(function (el) {
            if (el && el[key]) return el[key] === reference;
            return false;
        });
    },
    keyIncludes: function (db: any[], key: string, reference: any): any[] {
        return db.filter(function (el) {
            if (el[key]) return el[key].includes(reference);
        });
    },
    keysAre: function (db: any[], key1: string, value1: any, key2: string, value2: any): any[] {
        return db.filter(function (el) {
            const hasKey1 = el.hasOwnProperty(key1);
            const hasKey2 = el.hasOwnProperty(key2);
            if (hasKey1 && hasKey2) {
                return el[key1] === value1 && el[key2] === value2;
            }
            return false;
        });
    },
    subKeysAre: function (db: any[], sub: string, key1: string, value1: any, key2: string, value2: any): any[] {
        return db.filter(function (el) {
            if (el[sub]) {
                const hasKey1 = el[sub].hasOwnProperty(key1);
                const hasKey2 = el[sub].hasOwnProperty(key2);
                if (hasKey1 && hasKey2) {
                    return el[sub][key1] === value1 && el[sub][key2] === value2;
                }
            }
            return false;
        });
    },
    keyKeyIs: function (db: any[], key1: string, key2: string, reference: any): any[] {
        return db.filter(function (el) {
            if (el[key1] && el[key1][key2]) return el[key1][key2] == reference;
            return false;
        });
    },
    doubles: function (db: any[]): void {
        db = [...new Set(db)];
    },
    doublesXY: function (db: any[]): any[] {
        return db.filter((point, index, self) =>
            index === self.findIndex((p) => (
                p.x === point.x && p.y === point.y
            ))
        );
    },
    doublesKeyXY: function (db: any[], key: string): any[] {
        let caught: number[] = [];
        let unique: any[] = [];
        for (let i = 0; i < db.length; i++) {
            for (let j = i + 1; j < db.length; j++) {
                if ((db[i][key].x == db[j][key].x && db[i][key].y == db[j][key].y)) caught.push(j);
            }
        }
        for (let i = 0; i < db.length; i++) {
            if (!caught.includes(i)) unique.push(db[i]);
        }
        return unique;
    }
},

sort: {
    id: function (db_mess: any[], db_reference: any[]): any[] {
        if (db_mess.length !== db_reference.length) console.log("ERROR: not same array length")
        for (let i = 0; i < db_reference.length; i++) {
            const currentId = db_mess[i].id;
            const targetId = db_reference[i].id;
            if (currentId !== targetId) {
                const targetIndex = db_mess.findIndex(item => item.id === targetId);
                Arry.move.index(db_mess, i, targetIndex);
            }
        }
        return db_mess;
    },
    key: function (db: any[], key: string): void {
        db.sort((a, b) => a[key] - b[key]);
    },
    secondaryKey: function (db: any[], first: string, second: string = false as any): void {
        db.sort((a, b) => a[first] - b[first] || a[second] - b[second]);
    },
    keyKey: function (db: any[], key1: string, key2: string): void {
        db.sort((a, b) => a[key1][key2] - b[key1][key2]);
    },
    kids: function (db: any[], start_id: string = "root"): any[] {
        let output: any[] = [];
        function order(io: any): void {
            output.push(io);
            if (io.kids) {
                for (const k of io.kids) {
                    const kid = Arry.get.id(db, k);
                    if (kid) order(kid as any);
                }
            }
        }
        order(Arry.get.id(db, start_id) as any);
        return output;
    }
},

check: {
    id: function (array: any[], idToCheck: string): boolean {
        return array.some(item => item.id === idToCheck);
    },
    ids: function (array: any[], idsToCheck: string[]): boolean {
        return array.some(item => idsToCheck.includes(item.id!));
    },
    matching_strings: function (array_a: string[], array_b: string[]): boolean {
        if (array_a.length !== array_b.length) return false;
        for (let i = 0; i < array_a.length; i++) {
            if (array_a[i] !== array_b[i]) return false;
        }
        return true;
    },
    matching_numbers: function (array_a: number[], array_b: number[]): boolean {
        if (array_a.length !== array_b.length) return false;
        for (let i = 0; i < array_a.length; i++) {
            if (array_a[i] !== array_b[i]) return false;
        }
        return true;
    },
    offspring: function (params: OffspringCheckParams): boolean {
        const { ia, parent, key = "kids", id } = params;
        const parentObj = ia.find(item => item.id === parent);
        if (!parentObj) return false;
        const kids = parentObj[key] || [];
        if (kids.includes(id)) return true;
        for (const kidId of kids) {
            if (Arry.check.offspring({ ia, parent: kidId, key, id })) {
                return true;
            }
        }
        return false;
    },
    before: function (array: any[], id1: string, id2: string): boolean {
        const index1 = array.findIndex(obj => obj.id === id1);
        const index2 = array.findIndex(obj => obj.id === id2);
        if (index1 === -1 || index2 === -1) console.log("ERROR: object not found");
        return index1 < index2;
    },
    after: function (array: any[], id1: string, id2: string): boolean {
        const index1 = array.findIndex(obj => obj.id === id1);
        const index2 = array.findIndex(obj => obj.id === id2);
        if (index1 === -1 || index2 === -1) console.log("ERROR: object not found");
        return index1 > index2;
    }
},

add: {
    string: function (db: string[], string: string = "hello", at_end: boolean = true): void {
        for (let i = 0; i < db.length; i++) {
            db[i] = at_end ? db[i] + string : string + db[i];
        }
    }
},

insert: {
    after_id: function (db: any[], id: string, io: any): any[] {
        const i = Arry.get.index(db, id);
        if (i > -1) {
            db.splice(i + 1, 0, io);
        } else {
            db.push(io);
        }
        return db;
    },
    id_offset: function (params: IdOffsetParams): any[] {
        const { ia, id, io, offset = 1 } = params;
        const i = Arry.get.index(ia, id);
        if (i > -1) {
            let targetIndex = i + offset;
            targetIndex = Math.max(0, Math.min(targetIndex, ia.length));
            ia.splice(targetIndex, 0, io);
        } else {
            ia.push(io);
        }
        return ia;
    },
    after_string: function (db: string[], id: string, is: string): string[] {
        const ti = Arry.get.string_index(db, id);
        if (ti === -1) {
            if (db.indexOf(is) === -1) db.push(is);
            return db;
        }
        const ei = db.indexOf(is);
        if (ei > -1) db.splice(ei, 1);
        db.splice(ei > -1 && ei < ti ? ti : ti + 1, 0, is);
        return db;
    },
    before_id: function (db: any[], id: string, io: any): any[] {
        const i = Arry.get.index(db, id);
        if (i > -1) {
            db.splice(i, 0, io);
        } else {
            db.unshift(io);
        }
        return db;
    },
    before_string: function (db: string[], id: string, is: string): string[] {
        const ti = Arry.get.string_index(db, id);
        if (ti === -1) {
            if (db.indexOf(is) === -1) db.unshift(is);
            return db;
        }
        const ei = db.indexOf(is);
        if (ei > -1) db.splice(ei, 1);
        db.splice(ei > -1 && ei < ti ? ti - 1 : ti, 0, is);
        return db;
    }
}
};